import { clsx } from 'clsx';
import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, useSearchParams } from 'react-router-dom';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  PlusIcon,
} from '@heroicons/react/outline';
import { LocationCard } from '@mosey/components/layout/LocationCard';
import { MenuTransition } from '@mosey/components/menus/common/MenuTransition';
import { components } from '@mosey/api-types';
import { USStateName } from '@mosey/utils/constants/us-states';
import { useLegalEntityRegions, useUser } from '../../../hooks/useUser';

const MAX_REGIONS_BEFORE_OVERFLOW = 20;
const LOCATION_CARD_GAP = 8;
const CAROUSEL_X_PADDING = 4;

const getFittingElementsCount = (container: HTMLElement) => {
  if (window.innerWidth < 640) {
    return 0;
  }

  const containerWidth = container.clientWidth;
  const children = Array.from(container.children) as HTMLElement[];
  let totalWidth = CAROUSEL_X_PADDING;
  let count = 0;

  /**
   * Add the width of the "All", "Add location" links, and the "n more" dropdown
   * button, plus the gap between them.
   */
  const nonRegionLinks = container.querySelectorAll('[data-non-region-link]');

  for (let i = 0; i < nonRegionLinks.length; i++) {
    const link = nonRegionLinks[i];

    if (link instanceof HTMLElement) {
      totalWidth += link.offsetWidth + LOCATION_CARD_GAP;
    }
  }

  for (let i = 1; i < children.length - 1; i++) {
    const childWidth = children[i].offsetWidth;

    if (totalWidth + childWidth > containerWidth) {
      break;
    }

    // Need to account for the gap between elements
    totalWidth += childWidth + LOCATION_CARD_GAP;
    count++;
  }

  return count;
};

interface CarouselButtonProps {
  direction: 'previous' | 'next';
  onClick: () => void;
  show: boolean;
}

const CarouselButton = ({ direction, onClick, show }: CarouselButtonProps) => {
  const Icon = direction === 'next' ? ChevronRightIcon : ChevronLeftIcon;

  return (
    <>
      <div
        aria-hidden="true"
        className={clsx(
          'pointer-events-none absolute inset-y-0 w-20 from-white to-transparent transition-opacity',
          direction === 'next'
            ? 'right-[28px] bg-gradient-to-l'
            : 'left-[28px] bg-gradient-to-r',
          show ? 'opacity-100' : 'opacity-0',
        )}
      />
      <button
        type="button"
        className={clsx(
          'absolute top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full bg-transparent text-gray-500 outline-none hover:text-teal-800 focus-visible:ring-2 focus-visible:ring-teal-600',
          direction === 'next' ? 'right-1' : 'left-1',
          show ? 'block' : 'hidden',
        )}
        aria-label={`See ${direction} locations`}
        onClick={onClick}
      >
        <Icon className="size-4 " />
      </button>
    </>
  );
};

export const TaskOverviewLocationFilters = () => {
  const {
    legal_entity: { incorporation_region: incorporationRegion },
  } = useUser();
  const regions = useLegalEntityRegions();
  const [searchParams] = useSearchParams();
  const locationId = searchParams.get('location_id');
  const locationFilterRef = useRef<HTMLUListElement>(null);
  const [fittingElementsCount, setFittingElementsCount] = useState(
    regions.length,
  );
  const [lastLocationFilterWidth, setLastLocationFilterWidth] = useState(
    Number.MAX_SAFE_INTEGER,
  );
  const isLocationFilterOverflow = regions.length > MAX_REGIONS_BEFORE_OVERFLOW;
  const locationOverflowAmount = regions.length - fittingElementsCount;
  const [scrollPosition, setScrollPosition] = useState<
    'start' | 'middle' | 'end'
  >('start');

  useEffect(() => {
    const carouselEl = locationFilterRef.current;

    const calculateVisibility = () => {
      if (carouselEl) {
        const scrollLeft = carouselEl.scrollLeft;
        const maxScrollLeft = carouselEl.scrollWidth - carouselEl.clientWidth;

        if (scrollLeft === 0) {
          setScrollPosition('start');
        } else if (scrollLeft === maxScrollLeft) {
          setScrollPosition('end');
        } else {
          setScrollPosition('middle');
        }
      }
    };

    if (carouselEl) {
      carouselEl.addEventListener('scroll', calculateVisibility);
    }

    return () => {
      if (carouselEl) {
        carouselEl.removeEventListener('scroll', calculateVisibility);
      }
    };
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (isLocationFilterOverflow && locationFilterRef.current) {
      const resizeObserver = new ResizeObserver(([el]) => {
        requestAnimationFrame(() => {
          const newWidth = el.contentRect.width;

          if (newWidth > lastLocationFilterWidth) {
            setFittingElementsCount(regions.length);
          } else if (newWidth < lastLocationFilterWidth) {
            const count = getFittingElementsCount(el.target as HTMLElement);
            setFittingElementsCount(count);
          }

          setLastLocationFilterWidth(newWidth);
        });
      });

      if (locationFilterRef.current) {
        resizeObserver.observe(locationFilterRef.current);
      }

      const count = getFittingElementsCount(locationFilterRef.current);
      setFittingElementsCount(count);

      return () => {
        resizeObserver.disconnect();
      };
    }
  });

  const addLocationLink =
    regions.length < 51 ? (
      <Link
        to="/locations/usa/setup"
        className="flex shrink-0 grow items-center justify-center gap-x-2 rounded border border-teal-350 px-3 py-2 text-sm font-medium leading-6 text-teal-900 outline-none focus-visible:ring-2 focus-visible:ring-teal-800"
        data-non-region-link="true"
      >
        <PlusIcon className="size-4 shrink-0" />
        <div className="shrink-0 grow text-nowrap">Add location</div>
      </Link>
    ) : null;

  const toLocationCard = ({
    region,
  }: components['schemas']['LegalEntityRegionRef']) => {
    const code = region.code.toLowerCase();
    const isCurrentLocation = code === locationId;

    return (
      <li key={region.id}>
        <LocationCard
          region={region}
          to={`?location_id=${code}`}
          aria-current={isCurrentLocation ? 'location' : 'false'}
          aria-label={`Show ${region.name} tasks`}
          selected={isCurrentLocation}
          // TODO: Support other status; need all location API data
          status={
            incorporationRegion?.code === region.code ? 'home' : undefined
          }
        />
      </li>
    );
  };

  const getArrowClickHandler = (direction: 'previous' | 'next') => {
    return () => {
      const carouselEl = locationFilterRef.current;

      if (carouselEl) {
        carouselEl.scrollBy({
          left: carouselEl.clientWidth * (direction === 'next' ? 1 : -1),
          behavior: 'smooth',
        });
      }
    };
  };

  return (
    <div className="relative isolate box-border w-full max-w-full px-[30px] py-1">
      {!isLocationFilterOverflow && (
        <CarouselButton
          show={scrollPosition !== 'start'}
          onClick={getArrowClickHandler('previous')}
          direction="previous"
        />
      )}

      <ul
        ref={locationFilterRef}
        className={clsx(
          'flex w-full max-w-full grow-0 gap-2 px-[2px] py-5',
          isLocationFilterOverflow ? 'overflow-hidden' : 'overflow-x-auto',
        )}
      >
        <li>
          <NavLink
            aria-current={!locationId ? 'location' : 'false'}
            className={clsx(
              'flex shrink-0 grow items-center gap-x-2 rounded border px-4 py-2 text-sm font-medium outline-none hover:border-teal-500 hover:bg-teal-400 hover:text-black focus-visible:ring-2 focus-visible:ring-teal-800',
              !locationId
                ? 'border-teal-700 bg-teal-700 text-teal-50'
                : 'border-teal-350 bg-teal-300 text-zinc-800',
            )}
            to=""
            aria-label="Show tasks from all states"
            data-non-region-link="true"
          >
            <div className="leading-6">All</div>
          </NavLink>
        </li>

        {regions.slice(0, fittingElementsCount).map(toLocationCard)}

        {!isLocationFilterOverflow && <li>{addLocationLink}</li>}

        {isLocationFilterOverflow && (
          <li>
            <Popover as={Fragment}>
              {({ open }) => {
                const Icon = open ? ChevronUpIcon : ChevronDownIcon;
                const sliceIndex = fittingElementsCount - 1;

                return (
                  <>
                    <PopoverButton
                      className="flex h-full w-max items-center gap-x-2 rounded border border-teal-350 bg-teal-300 py-2 pl-2 pr-3 text-sm font-medium text-zinc-800 outline-none hover:border-teal-500 hover:bg-teal-400 hover:text-black focus-visible:ring-2 focus-visible:ring-teal-800"
                      data-non-region-link="true"
                    >
                      <div className="shrink-0">
                        <span className="hidden sm:block">
                          {locationOverflowAmount} more
                        </span>
                        <span className="sm:hidden">
                          {locationId
                            ? USStateName[
                                locationId.toUpperCase() as keyof typeof USStateName
                              ]
                            : 'Filter by location'}
                        </span>
                      </div>

                      <Icon className="size-4 shrink-0" />
                    </PopoverButton>

                    <MenuTransition>
                      <PopoverPanel
                        anchor={{ to: 'bottom', gap: 8, padding: 8 }}
                        as="ul"
                        className="grid w-full grid-cols-1 gap-2 rounded border border-gray-200 bg-white p-4 shadow-lg sm:w-auto sm:grid-cols-5"
                      >
                        {regions
                          .slice(sliceIndex < 0 ? 0 : sliceIndex)
                          .map(toLocationCard)}

                        {addLocationLink}
                      </PopoverPanel>
                    </MenuTransition>
                  </>
                );
              }}
            </Popover>
          </li>
        )}
      </ul>

      {!isLocationFilterOverflow && (
        <CarouselButton
          show={scrollPosition !== 'end'}
          direction="next"
          onClick={getArrowClickHandler('next')}
        />
      )}
    </div>
  );
};
